import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { StandaloneSearchBox } from '@react-google-maps/api';
import { parseStringToInt } from 'src/utils';
import { MapCoordinates } from 'src/components/pages/mymes/editor/blobs/editor/context';
import { useContentContext } from 'src/components/pages/mymes/editor/contentStep/ContentContext';

type Map = google.maps.Map;
type Props = {
  blobId?: string;
  moduleId?: string;
};

const initialMapCenterPointBeforeGeoLocationPosition = {
  lat: 48.5,
  lng: 23.383
}; // coordinates of the center of europe

const initialZoom = 4;
const zoomOnTarget = 15;

const radiusOptions = [
  { text: '10', value: 10 },
  { text: '25', value: 25 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '200', value: 200 }
];

export const useMapLogic = ({ blobId, moduleId }: Props) => {
  const [map, setMap] = useState<Map | null>(null);
  const [centerPoint, setCenterPoint] = useState<MapCoordinates>(initialMapCenterPointBeforeGeoLocationPosition);
  const [marker, setMarker] = useState<google.maps.LatLng | null>(null);
  const [zoom, setZoom] = useState(initialZoom);

  const searchBoxRef = useRef<StandaloneSearchBox | null>(null);

  const { modules } = useContentContext();
  const moduleIndex = modules.findIndex(currentModule => currentModule.id === moduleId);
  const { location } = modules[moduleIndex].blobs.find(currentBlob => currentBlob.id === blobId) || {};
  const { radiusInMeters, latitude, longitude } = location || {};

  const [radius, setRadius] = useState<number>(radiusInMeters || 50);

  const onLoad = useCallback((map: Map) => {
    setMap(map);

    if (latitude && longitude) {
      const marker = new google.maps.LatLng(latitude, longitude);
      setMarker(marker);
      setCenterPoint({ lat: latitude, lng: longitude });
      setZoom(zoomOnTarget);
      return;
    }

    navigator.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
      setCenterPoint({ lat: latitude, lng: longitude });
    });
  }, []);

  const onUnmount = useCallback((_map: Map) => {
    setMap(null);
    setMarker(null);
  }, []);

  const onPlacesChanged = () => {
    const placesFound = searchBoxRef.current?.state.searchBox?.getPlaces();

    if (placesFound?.length === 1) {
      const exactPlaceLocation = placesFound[0].geometry?.location;

      if (exactPlaceLocation) {
        setCenterPoint({ lat: exactPlaceLocation.lat(), lng: exactPlaceLocation.lng() });
        setZoom(zoomOnTarget);
      }
    }
  };

  const handleSetMarkerOnTheMap = (e: google.maps.MapMouseEvent) => setMarker(e.latLng);

  const handleRadiusChange = (e: ChangeEvent<HTMLInputElement>) => setRadius(parseStringToInt(e.target.value));

  return {
    map,
    centerPoint,
    marker,
    radius,
    zoom,
    searchBoxRef,
    radiusOptions,
    onLoad,
    onUnmount,
    onPlacesChanged,
    handleSetMarkerOnTheMap,
    handleRadiusChange,
    setMarker,
    setMap
  };
};
